import { useEffect, useRef, useState } from "react";
import { useGoogleMaps } from "@/hooks/useGoogleMaps";
import {
  HANDLES, bearingFrom, handlePoints, metersFromDelta, radToDeg, rectToRing, resizeRect,
  ringToRect, transformRing, translateRect,
} from "@/lib/parkingGeometry";
import { cn } from "@/lib/utils";
import { MapPinOff } from "lucide-react";

// Satellite map for parking lots: slot polygons coloured by occupancy, the
// outlines of each scanned area, and the four draft corners while an area is
// being drawn.
//
// Three interaction modes (`interaction`):
//   "view"   slots are clickable / right-clickable, nothing is editable
//   "slots"  the selected slot becomes a rectangle editor: drag its body to
//            move it, its edge/corner handles to resize along its own two axes,
//            and the knob in its centre to rotate it to any angle
//   "area"   the area named by `areaTool` is lifted out as one piece: drag it to
//            move it, drag the knob in its centre to rotate it
// Slots stay rectangles throughout — there is deliberately no free vertex
// editing, because a hand-dragged vertex turns a stall into an arbitrary quad.
//
// The map only reports gestures (on release); what they mean and when they are
// saved is the caller's business. Uses only the core Maps API (Map / Polygon /
// Marker), like MapCanvas — the `drawing` library is deliberately not loaded
// (see lib/googleMaps.js).
//
// Plain "satellite", not "hybrid": painted stall lines are the thing being
// matched against, and hybrid's road/label overlay sits on top of them.

const COLOR = {
  vacant: "#22c55e",
  occupied: "#ef4444",
  preview: "#38bdf8",
};
const LABEL_MIN_ZOOM = 20; // below this, numbers on 200 slots are just noise
const MIN_KNOB_DRAG_M = 0.2; // ignore the knob until it is dragged clear of its own centre

const toPath = (ring) => ring.map(([lat, lng]) => ({ lat, lng }));

function slotTone(slot) {
  if (slot.preview) return "preview";
  return slot.occupied ? "occupied" : "vacant";
}

function ringCenter(ring) {
  return {
    lat: ring.reduce((s, p) => s + p[0], 0) / ring.length,
    lng: ring.reduce((s, p) => s + p[1], 0) / ring.length,
  };
}

// Everything that affects how a slot is drawn. Overlays are only rebuilt when
// this changes.
const sigOf = (slot, selected) =>
  JSON.stringify([slot.polygon, slotTone(slot), !!selected, slot.number]);

// A round "rotate" button, as an inline SVG marker icon.
const KNOB_SVG = encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">' +
  '<circle cx="15" cy="15" r="13" fill="#111827" stroke="#ffffff" stroke-width="2"/>' +
  '<path d="M21 15a6 6 0 1 1-2-4.5" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round"/>' +
  '<path d="M20 7v4.5h-4.5" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
  "</svg>"
);

// A name tag for an area: a dark pill with the name in white, as an inline SVG
// marker icon. Sized from the text length because an SVG icon has no auto-fit.
const escapeXml = (t) => t.replace(/[<>&"']/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&#39;" }[c]));

function areaLabelIcon(maps, text) {
  const shown = text.length > 28 ? `${text.slice(0, 27)}…` : text;
  const w = Math.round(22 + shown.length * 7.6);
  const h = 24;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">` +
    `<rect x="1" y="1" width="${w - 2}" height="${h - 2}" rx="12" fill="#111827" fill-opacity="0.82" stroke="#fbbf24" stroke-width="1.5"/>` +
    `<text x="${w / 2}" y="16" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" font-weight="700" fill="#ffffff">${escapeXml(shown)}</text>` +
    "</svg>";
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new maps.Size(w, h),
    anchor: new maps.Point(w / 2, h / 2),
  };
}

export function ParkingMap({
  center,
  zoom = 19,
  slots = [],
  corners = [],
  outlines = [],
  interaction = "view",
  selectedSlotId = null,
  areaTool = null, // { areaId, center: [lat, lng] }, with interaction "area"
  editableCorners = false,
  onMapClick,
  onSlotClick,
  onSlotContextMenu, // (slotId, { x, y }) in pixels relative to the map
  onSlotChange, // (slotId, ring) — a slot rectangle was moved / resized / rotated
  onCornerMove,
  onAreaMove, // ({ east, north }) metres — the area was dragged
  onAreaRotate, // (deltaDeg) — the area was rotated about its centre
  fitKey,
  className,
  testId,
}) {
  const { maps, error } = useGoogleMaps();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const slotOverlays = useRef(new Map()); // slot id -> { sig, polygon, label }
  const cornerOverlays = useRef([]);
  const outlineOverlays = useRef([]);
  const editorRef = useRef(null); // { destroy } for the active rectangle / area editor
  // Flips once the map instance exists, so the overlay effects below run after
  // it does (a ref change alone would not re-run them).
  const [ready, setReady] = useState(false);

  // Latest callbacks, so listeners attached once never call a stale closure.
  const cb = useRef({});
  cb.current = {
    onMapClick, onSlotClick, onSlotContextMenu, onSlotChange, onCornerMove, onAreaMove, onAreaRotate,
  };

  // Which slots the editor is drawing itself, so the base layer leaves them out.
  const editingSlot = interaction === "slots" ? slots.find((s) => s.id === selectedSlotId) : null;
  const editingAreaId = interaction === "area" && areaTool ? areaTool.areaId : null;
  const baseSlots = slots.filter((s) => s !== editingSlot && (editingAreaId == null || s.areaId !== editingAreaId));

  // Create the map once.
  useEffect(() => {
    if (!maps || !containerRef.current || mapRef.current) return;
    const map = new maps.Map(containerRef.current, {
      center: center || { lat: 39.5, lng: -98.35 },
      zoom: center ? zoom : 4,
      mapTypeId: "satellite",
      tilt: 0,
      clickableIcons: false,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: true,
      gestureHandling: "greedy",
    });
    map.addListener("click", (e) => cb.current.onMapClick?.({ lat: e.latLng.lat(), lng: e.latLng.lng() }));
    map.addListener("zoom_changed", () => {
      const show = map.getZoom() >= LABEL_MIN_ZOOM;
      slotOverlays.current.forEach(({ label }) => label.setMap(show ? map : null));
    });
    mapRef.current = map;
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maps]);

  // Pixel position of a right-click, relative to the map's own box.
  const menuPoint = (e) => {
    const dom = e?.domEvent;
    const box = containerRef.current?.getBoundingClientRect();
    if (!dom || !box || dom.clientX == null) return null;
    dom.preventDefault?.();
    return { x: dom.clientX - box.left, y: dom.clientY - box.top };
  };

  // Base layer — every slot not being edited, reconciled by id rather than
  // rebuilt wholesale.
  useEffect(() => {
    const map = mapRef.current;
    if (!maps || !map) return;
    const overlays = slotOverlays.current;
    const next = new Map();
    const showLabels = map.getZoom() >= LABEL_MIN_ZOOM;

    const destroy = (rec) => {
      maps.event.clearInstanceListeners(rec.polygon);
      rec.polygon.setMap(null);
      rec.label.setMap(null);
    };

    const build = (slot, selected) => {
      const color = COLOR[slotTone(slot)];
      const polygon = new maps.Polygon({
        map,
        paths: toPath(slot.polygon),
        fillColor: color,
        fillOpacity: selected ? 0.65 : 0.4,
        strokeColor: selected ? "#ffffff" : color,
        strokeOpacity: 0.95,
        strokeWeight: selected ? 3 : 1,
        zIndex: selected ? 3 : 2,
        clickable: true,
      });
      const label = new maps.Marker({
        map: showLabels ? map : null,
        position: ringCenter(slot.polygon),
        clickable: false,
        icon: { path: maps.SymbolPath.CIRCLE, scale: 0 },
        label: { text: String(slot.number), color: "#ffffff", fontSize: "10px", fontWeight: "600" },
      });
      polygon.addListener("click", () => cb.current.onSlotClick?.(slot.id));
      polygon.addListener("rightclick", (e) => {
        const point = menuPoint(e);
        if (point) cb.current.onSlotContextMenu?.(slot.id, point);
      });
      return { sig: sigOf(slot, selected), polygon, label };
    };

    baseSlots.forEach((slot) => {
      const selected = slot.id === selectedSlotId;
      const prev = overlays.get(slot.id);
      if (prev && prev.sig === sigOf(slot, selected)) {
        next.set(slot.id, prev);
        return;
      }
      if (prev) destroy(prev);
      next.set(slot.id, build(slot, selected));
    });
    overlays.forEach((rec, id) => {
      if (!next.has(id)) destroy(rec);
    });
    slotOverlays.current = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maps, ready, slots, selectedSlotId, interaction, areaTool]);

  // Editor layer: the one thing currently being edited.
  const editingSlotSig = editingSlot ? JSON.stringify([editingSlot.id, editingSlot.polygon, slotTone(editingSlot)]) : null;
  const areaRings = editingAreaId == null ? [] : slots.filter((s) => s.areaId === editingAreaId).map((s) => s.polygon);
  const areaSig = editingAreaId == null ? null : JSON.stringify([editingAreaId, areaTool.center, areaRings]);

  useEffect(() => {
    const map = mapRef.current;
    if (!maps || !map) return undefined;
    editorRef.current?.destroy();
    editorRef.current = null;
    if (editingSlot) editorRef.current = mountRectEditor(maps, map, editingSlot, cb, menuPoint);
    else if (editingAreaId != null && areaRings.length) {
      editorRef.current = mountAreaEditor(maps, map, areaRings, areaTool.center, cb, menuPoint);
    }
    return () => {
      editorRef.current?.destroy();
      editorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maps, ready, editingSlotSig, areaSig]);

  // Remove everything on unmount.
  useEffect(() => () => {
    slotOverlays.current.forEach(({ polygon, label }) => {
      polygon.setMap(null);
      label.setMap(null);
    });
    slotOverlays.current = new Map();
  }, []);

  // Outlines of the scanned areas: context only, never clickable.
  useEffect(() => {
    const map = mapRef.current;
    if (!maps || !map) return undefined;
    outlineOverlays.current.forEach((o) => o.setMap(null));
    outlineOverlays.current = outlines
      .filter((o) => o.corners?.length >= 3)
      .flatMap((o) => {
        const parts = [new maps.Polygon({
          map, paths: toPath(o.corners), clickable: false, zIndex: 0,
          strokeColor: "#fbbf24", strokeOpacity: 0.55, strokeWeight: 1,
          fillOpacity: 0,
        })];
        // Name tag in the middle of the area, so each patch of a big lot can be
        // told apart at a glance.
        if (o.name) {
          parts.push(new maps.Marker({
            map, position: ringCenter(o.corners), clickable: false, zIndex: 8,
            icon: areaLabelIcon(maps, o.name),
          }));
        }
        return parts;
      });
    return () => {
      outlineOverlays.current.forEach((o) => o.setMap(null));
      outlineOverlays.current = [];
    };
  }, [maps, ready, outlines]);

  // Draft corners: numbered pins plus the outline they make.
  useEffect(() => {
    const map = mapRef.current;
    if (!maps || !map) return undefined;
    cornerOverlays.current.forEach((o) => o.setMap(null));
    cornerOverlays.current = [];
    if (corners.length === 0) return undefined;

    cornerOverlays.current.push(
      new maps.Polygon({
        map, paths: toPath(corners), clickable: false, zIndex: 1,
        strokeColor: "#fbbf24", strokeOpacity: 0.95, strokeWeight: 2,
        fillColor: "#fbbf24", fillOpacity: corners.length === 4 ? 0.06 : 0,
      })
    );
    corners.forEach(([lat, lng], i) => {
      const marker = new maps.Marker({
        map, position: { lat, lng }, zIndex: 5,
        clickable: editableCorners, draggable: editableCorners,
        label: { text: String(i + 1), color: "#111827", fontSize: "11px", fontWeight: "700" },
        icon: {
          path: maps.SymbolPath.CIRCLE, scale: 11,
          fillColor: "#fbbf24", fillOpacity: 1, strokeColor: "#111827", strokeWeight: 2,
        },
      });
      if (editableCorners) {
        // Only on release: the grid is regenerated server-side, and doing that
        // for every pixel of a drag would be a request storm.
        marker.addListener("dragend", (e) => cb.current.onCornerMove?.(i, { lat: e.latLng.lat(), lng: e.latLng.lng() }));
      }
      cornerOverlays.current.push(marker);
    });
    return () => {
      cornerOverlays.current.forEach((o) => {
        maps.event.clearInstanceListeners(o);
        o.setMap(null);
      });
      cornerOverlays.current = [];
    };
  }, [maps, ready, corners, editableCorners]);

  // Re-frame when the caller says the content changed (a different layout
  // opened, a preview appeared) — not on every occupancy update or edit, which
  // would yank the view out from under someone who is panning.
  useEffect(() => {
    const map = mapRef.current;
    if (!maps || !map) return;
    const points = [
      ...slots.flatMap((s) => s.polygon),
      ...corners,
      ...outlines.flatMap((o) => o.corners || []),
    ];
    if (points.length === 0) {
      if (center) {
        map.setCenter(center);
        map.setZoom(zoom);
      }
      return;
    }
    const bounds = new maps.LatLngBounds();
    points.forEach(([lat, lng]) => bounds.extend({ lat, lng }));
    map.fitBounds(bounds, 48);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maps, ready, fitKey]);

  if (error) {
    return (
      <div className={cn("flex h-full min-h-[240px] items-center justify-center rounded-2xl border border-border/60 bg-card/40 p-6 text-center", className)}>
        <div className="space-y-2 text-sm text-muted-foreground">
          <MapPinOff className="mx-auto h-6 w-6" />
          <p>The map could not be loaded.</p>
          <p className="text-xs">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      className={cn("h-full min-h-[240px] w-full overflow-hidden rounded-2xl border border-border/60 bg-secondary/30", className)}
    />
  );
}

// ------------------------------------------------------------------ editors
//
// Both editors keep their working state in closure variables and redraw the
// overlays directly during a gesture (no React re-render per mouse move), then
// report the result once, on release.

const squareHandleIcon = () => ({
  path: "M -1 -1 L 1 -1 L 1 1 L -1 1 Z",
  scale: 5,
  fillColor: "#ffffff",
  fillOpacity: 1,
  strokeColor: "#111827",
  strokeWeight: 1.5,
});

const knobIcon = (maps) => ({
  url: `data:image/svg+xml;charset=UTF-8,${KNOB_SVG}`,
  scaledSize: new maps.Size(30, 30),
  anchor: new maps.Point(15, 15),
});

// One slot, edited as a rectangle: body drag = move, edge/corner handles =
// resize on the rectangle's own axes, centre knob = rotate.
function mountRectEditor(maps, map, slot, cb, menuPoint) {
  const color = COLOR[slotTone(slot)];
  let rect = ringToRect(slot.polygon);
  let startRect = rect;

  const polygon = new maps.Polygon({
    map, paths: toPath(rectToRing(rect)), draggable: true, clickable: true, zIndex: 4,
    fillColor: color, fillOpacity: 0.55, strokeColor: "#ffffff", strokeOpacity: 1, strokeWeight: 2,
  });
  const handles = handlePoints(rect).map((h) => new maps.Marker({
    map, position: { lat: h.lat, lng: h.lng }, draggable: true, zIndex: 6, icon: squareHandleIcon(),
  }));
  const knob = new maps.Marker({
    map, position: { lat: rect.lat, lng: rect.lng }, draggable: true, zIndex: 7, icon: knobIcon(maps),
  });

  const draw = (except = null) => {
    polygon.setPath(toPath(rectToRing(rect)));
    handlePoints(rect).forEach((h, i) => {
      if (handles[i] !== except) handles[i].setPosition({ lat: h.lat, lng: h.lng });
    });
    if (knob !== except) knob.setPosition({ lat: rect.lat, lng: rect.lng });
  };
  const commit = () => {
    draw();
    cb.current.onSlotChange?.(slot.id, rectToRing(rect));
  };
  const setHandlesVisible = (visible) => {
    handles.forEach((m) => m.setMap(visible ? map : null));
    knob.setMap(visible ? map : null);
  };

  // Resize: anchored on the rectangle as it was when the drag began.
  handles.forEach((marker, i) => {
    const [sx, sy] = HANDLES[i];
    marker.addListener("dragstart", () => { startRect = rect; });
    marker.addListener("drag", (e) => {
      rect = resizeRect(startRect, sx, sy, [e.latLng.lat(), e.latLng.lng()]);
      draw(marker);
    });
    marker.addListener("dragend", commit);
  });

  // Rotate: the knob starts on the centre, so there is no angle until it has
  // been dragged clear of it; rotation is then the change in bearing since.
  let knobStartAngle = null;
  knob.addListener("dragstart", () => { startRect = rect; knobStartAngle = null; });
  knob.addListener("drag", (e) => {
    const { angle, distanceM } = bearingFrom([startRect.lat, startRect.lng], [e.latLng.lat(), e.latLng.lng()]);
    if (distanceM < MIN_KNOB_DRAG_M) return;
    if (knobStartAngle == null) knobStartAngle = angle;
    rect = { ...startRect, theta: startRect.theta + (angle - knobStartAngle) };
    draw(knob);
  });
  knob.addListener("dragend", commit);

  // Move: drag the body. The handles are hidden while it travels (they are
  // separate markers and would be left behind), and redrawn on release.
  let bodyStart = null;
  polygon.addListener("dragstart", () => {
    bodyStart = polygon.getPath().getAt(0);
    setHandlesVisible(false);
  });
  polygon.addListener("dragend", () => {
    const now = polygon.getPath().getAt(0);
    rect = translateRect(rect, now.lat() - bodyStart.lat(), now.lng() - bodyStart.lng());
    setHandlesVisible(true);
    commit();
  });
  polygon.addListener("rightclick", (e) => {
    const point = menuPoint(e);
    if (point) cb.current.onSlotContextMenu?.(slot.id, point);
  });

  return {
    destroy() {
      [polygon, knob, ...handles].forEach((o) => {
        maps.event.clearInstanceListeners(o);
        o.setMap(null);
      });
    },
  };
}

// A whole area lifted out as one piece: one multi-ring polygon (so a native
// drag moves every slot together, with live feedback) and a centre knob that
// rotates it.
function mountAreaEditor(maps, map, rings, centre, cb, menuPoint) {
  let liveRings = rings;
  const polygon = new maps.Polygon({
    map, paths: rings.map(toPath), draggable: true, clickable: true, zIndex: 4,
    fillColor: "#fbbf24", fillOpacity: 0.35, strokeColor: "#fbbf24", strokeOpacity: 1, strokeWeight: 2,
  });
  const knob = new maps.Marker({
    map, position: { lat: centre[0], lng: centre[1] }, draggable: true, zIndex: 7, icon: knobIcon(maps),
  });

  // Move.
  let startVertex = null;
  polygon.addListener("dragstart", () => {
    startVertex = polygon.getPaths().getAt(0).getAt(0);
    knob.setMap(null);
  });
  polygon.addListener("dragend", () => {
    const now = polygon.getPaths().getAt(0).getAt(0);
    const { east, north } = metersFromDelta(centre[0], now.lat() - startVertex.lat(), now.lng() - startVertex.lng());
    knob.setMap(map);
    cb.current.onAreaMove?.({ east, north });
  });
  polygon.addListener("rightclick", (e) => {
    const point = menuPoint(e);
    if (point) cb.current.onSlotContextMenu?.(null, point);
  });

  // Rotate about the area's centre.
  let startAngle = null;
  let deltaDeg = 0;
  knob.addListener("dragstart", () => { startAngle = null; deltaDeg = 0; });
  knob.addListener("drag", (e) => {
    const { angle, distanceM } = bearingFrom(centre, [e.latLng.lat(), e.latLng.lng()]);
    if (distanceM < MIN_KNOB_DRAG_M) return;
    if (startAngle == null) startAngle = angle;
    deltaDeg = radToDeg(angle - startAngle);
    liveRings = rings.map((r) => transformRing(r, centre, deltaDeg));
    polygon.setPaths(liveRings.map(toPath));
  });
  knob.addListener("dragend", () => {
    knob.setPosition({ lat: centre[0], lng: centre[1] });
    if (deltaDeg) cb.current.onAreaRotate?.(deltaDeg);
  });

  return {
    destroy() {
      [polygon, knob].forEach((o) => {
        maps.event.clearInstanceListeners(o);
        o.setMap(null);
      });
    },
  };
}
